import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ScrollText,
  Search,
  RefreshCw,
  Loader2,
  User,
  Calendar,
  Activity,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  X,
  ArrowRightLeft,
  LogIn,
  LogOut,
  Trash2,
  Plus,
  Edit,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatDateTime } from '@/lib/utils';
import api from '@/services/api';

interface AuditLog {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  description?: string;
  userId: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  ipAddress?: string;
  userAgent?: string;
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  createdAt: string;
}

interface AuditUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const actionColors: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  UPDATE: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  LOGIN: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  LOGOUT: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  EXPORT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  IMPORT: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  VIEW: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  ASSIGN: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  TRANSFER: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  APPROVE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  REJECT: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400',
};

const severityIcons: Record<AuditLog['severity'], React.ReactNode> = {
  INFO: <Info className="w-4 h-4 text-blue-500" />,
  WARNING: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
  ERROR: <XCircle className="w-4 h-4 text-red-500" />,
  CRITICAL: <AlertTriangle className="w-4 h-4 text-red-600" />,
};

const getActionIcon = (action: string) => {
  switch (action) {
    case 'CREATE': return <Plus className="w-4 h-4" />;
    case 'UPDATE': return <Edit className="w-4 h-4" />;
    case 'DELETE': return <Trash2 className="w-4 h-4" />;
    case 'LOGIN': return <LogIn className="w-4 h-4" />;
    case 'LOGOUT': return <LogOut className="w-4 h-4" />;
    case 'VIEW': return <Eye className="w-4 h-4" />;
    case 'ASSIGN': return <User className="w-4 h-4" />;
    case 'TRANSFER': return <ArrowRightLeft className="w-4 h-4" />;
    case 'APPROVE': return <CheckCircle className="w-4 h-4" />;
    case 'REJECT': return <XCircle className="w-4 h-4" />;
    default: return <Activity className="w-4 h-4" />;
  }
};

export default function AuditLogsPage() {
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const limit = 20;

  // Fetch audit logs
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['audit-logs', search, actionFilter, entityFilter, userFilter, startDate, endDate, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (actionFilter) params.append('action', actionFilter);
      if (entityFilter) params.append('entityType', entityFilter);
      if (userFilter) params.append('userId', userFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const response = await api.get(`/audit-logs?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch users for filter dropdown
  const { data: usersData } = useQuery({
    queryKey: ['audit-users'],
    queryFn: async () => {
      const response = await api.get('/audit-logs/users');
      return response.data as AuditUser[];
    },
  });

  // Fetch entity types
  const { data: entityTypes } = useQuery({
    queryKey: ['audit-entity-types'],
    queryFn: async () => {
      const response = await api.get('/audit-logs/entity-types');
      return response.data as string[];
    },
  });

  // Fetch actions
  const { data: actions } = useQuery({
    queryKey: ['audit-actions'],
    queryFn: async () => {
      const response = await api.get('/audit-logs/actions');
      return response.data as string[];
    },
  });

  const logs: AuditLog[] = data?.data || [];
  const pagination = data?.pagination || { page: 1, limit, total: 0, totalPages: 1 };

  const hasActiveFilters = useMemo(() => {
    return search || actionFilter || entityFilter || userFilter || startDate || endDate;
  }, [search, actionFilter, entityFilter, userFilter, startDate, endDate]);

  const clearFilters = () => {
    setSearch('');
    setActionFilter('');
    setEntityFilter('');
    setUserFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const toggleRowExpansion = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (actionFilter) params.append('action', actionFilter);
      if (entityFilter) params.append('entityType', entityFilter);
      if (userFilter) params.append('userId', userFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await api.get(`/audit-logs/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const renderValueDiff = (oldValues: Record<string, unknown> | null | undefined, newValues: Record<string, unknown> | null | undefined) => {
    if (!oldValues && !newValues) return null;

    const allKeys = new Set([
      ...Object.keys(oldValues || {}),
      ...Object.keys(newValues || {}),
    ]);

    const changes: { key: string; old: unknown; new: unknown }[] = [];
    allKeys.forEach(key => {
      const oldVal = oldValues?.[key];
      const newVal = newValues?.[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({ key, old: oldVal, new: newVal });
      }
    });

    if (changes.length === 0 && !oldValues && newValues) {
      return (
        <div className="space-y-1">
          {Object.entries(newValues).map(([key, value]) => (
            <div key={key} className="flex gap-2 text-sm">
              <span className="font-medium text-muted-foreground">{key}:</span>
              <span className="text-green-600 dark:text-green-400">{String(value)}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {changes.map(({ key, old, new: newVal }) => (
          <div key={key} className="text-sm">
            <span className="font-medium text-muted-foreground">{key}:</span>
            <div className="ml-4 flex flex-col gap-1">
              {old !== undefined && (
                <span className="text-red-600 dark:text-red-400 line-through">
                  {typeof old === 'object' ? JSON.stringify(old) : String(old)}
                </span>
              )}
              {newVal !== undefined && (
                <span className="text-green-600 dark:text-green-400">
                  {typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ScrollText className="w-8 h-8" />
            Audit Logs
          </h1>
          <p className="text-muted-foreground">
            View system audit trail and security events
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowFilters(!showFilters)}
            className={cn(hasActiveFilters && 'border-primary')}
          >
            <Filter className={cn('w-4 h-4 mr-2', hasActiveFilters && 'text-primary')} />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                !
              </span>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn('w-4 h-4 mr-2', isFetching && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {/* Search */}
              <div className="lg:col-span-2">
                <Label htmlFor="search" className="text-sm font-medium">Search</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Search by entity, user, IP..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* User Filter */}
              <div>
                <Label htmlFor="user" className="text-sm font-medium">User</Label>
                <select
                  id="user"
                  className="flex h-10 w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={userFilter}
                  onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Users</option>
                  {usersData?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action Filter */}
              <div>
                <Label htmlFor="action" className="text-sm font-medium">Action</Label>
                <select
                  id="action"
                  className="flex h-10 w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={actionFilter}
                  onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Actions</option>
                  {actions?.map((action) => (
                    <option key={action} value={action}>{action}</option>
                  ))}
                </select>
              </div>

              {/* Entity Type Filter */}
              <div>
                <Label htmlFor="entityType" className="text-sm font-medium">Entity Type</Label>
                <select
                  id="entityType"
                  className="flex h-10 w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={entityFilter}
                  onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Types</option>
                  {entityTypes?.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div>
                <Label htmlFor="startDate" className="text-sm font-medium">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="mt-1"
                />
              </div>

              {/* End Date */}
              <div>
                <Label htmlFor="endDate" className="text-sm font-medium">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="mt-1"
                />
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-4 flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear all filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Search (always visible) */}
      {!showFilters && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Quick search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
          />
        </div>
      )}

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Audit Trail ({pagination.total.toLocaleString()} records)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              Failed to load audit logs. Please try again.
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ScrollText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No audit logs found</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div key={log.id} className="border rounded-lg">
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleRowExpansion(log.id)}
                  >
                    <div className="flex-shrink-0">
                      {severityIcons[log.severity]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn(
                          'px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1',
                          actionColors[log.action] || 'bg-gray-100 text-gray-800'
                        )}>
                          {getActionIcon(log.action)}
                          {log.action}
                        </span>
                        <span className="text-sm font-medium">{log.entityType}</span>
                        {log.entityName && (
                          <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                            - {log.entityName}
                          </span>
                        )}
                      </div>
                      {log.description && (
                        <p className="text-sm text-muted-foreground mt-1 truncate">
                          {log.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                        {log.user && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.user.firstName} {log.user.lastName}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDateTime(log.createdAt)}
                        </span>
                        {log.ipAddress && (
                          <span className="hidden md:inline text-xs font-mono">
                            {log.ipAddress}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {expandedRows.has(log.id) ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </div>

                  {expandedRows.has(log.id) && (
                    <div className="px-4 pb-4 border-t bg-muted/30">
                      <div className="pt-4 grid gap-4 md:grid-cols-2">
                        <div>
                          <h4 className="text-sm font-medium mb-2">Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Log ID:</span>
                              <span className="font-mono text-xs">{log.id}</span>
                            </div>
                            {log.entityId && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Entity ID:</span>
                                <span className="font-mono text-xs">{log.entityId}</span>
                              </div>
                            )}
                            {log.user && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">User Email:</span>
                                <span className="text-xs">{log.user.email}</span>
                              </div>
                            )}
                            {log.ipAddress && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">IP Address:</span>
                                <span className="font-mono">{log.ipAddress}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Severity:</span>
                              <span className={cn(
                                'px-2 py-0.5 rounded text-xs',
                                log.severity === 'INFO' && 'bg-blue-100 text-blue-800',
                                log.severity === 'WARNING' && 'bg-yellow-100 text-yellow-800',
                                log.severity === 'ERROR' && 'bg-red-100 text-red-800',
                                log.severity === 'CRITICAL' && 'bg-red-200 text-red-900',
                              )}>
                                {log.severity}
                              </span>
                            </div>
                            {log.userAgent && (
                              <div>
                                <span className="text-muted-foreground">User Agent:</span>
                                <p className="text-xs mt-1 break-all text-muted-foreground/70">
                                  {log.userAgent}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {(log.oldValues || log.newValues) && (
                          <div>
                            <h4 className="text-sm font-medium mb-2">Changes</h4>
                            <div className="p-3 bg-background rounded border">
                              {renderValueDiff(log.oldValues, log.newValues)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(pagination.totalPages)}
                  disabled={page === pagination.totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
